import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button, Card, Input, OTPInput } from "@nutrayan/ui";
import {
  adminRequestPasswordSetup,
  adminConfirmPasswordSetup,
  adminRequestPasswordReset,
  adminConfirmPasswordReset,
  adminRequestUnlock,
  adminConfirmUnlock,
} from "@/lib/admin-auth";
import { PasswordField } from "@/components/PasswordField";
import { PasswordStrength } from "@/components/PasswordStrength";
import { assessPassword, policyViolationMessage } from "@/lib/password-policy";

export type CredentialVariant = "setup" | "forgot" | "unlock";

interface VariantConfig {
  title: string;
  intro: string;
  needsPassword: boolean;
  request: (email: string) => Promise<{ message?: string }>;
  confirm: (email: string, code: string, newPassword: string) => Promise<{ message?: string }>;
}

const VARIANTS: Record<CredentialVariant, VariantConfig> = {
  setup: {
    title: "Set your password",
    intro:
      "Your account was created by an administrator. Enter your email to receive a one-time code, then choose a password.",
    needsPassword: true,
    request: adminRequestPasswordSetup,
    confirm: adminConfirmPasswordSetup,
  },
  forgot: {
    title: "Reset your password",
    intro: "Enter your email to receive a one-time code, then choose a new password.",
    needsPassword: true,
    request: adminRequestPasswordReset,
    confirm: adminConfirmPasswordReset,
  },
  unlock: {
    title: "Unlock your account",
    intro: "Enter your email to receive a one-time unlock code.",
    needsPassword: false,
    request: adminRequestUnlock,
    confirm: (email, code) => adminConfirmUnlock(email, code),
  },
};

interface Props {
  variant: CredentialVariant;
  initialEmail?: string;
  /** Return to the password login screen. */
  onBack: () => void;
  /** Called after a successful confirm (password set / reset / account unlocked). */
  onDone: (email: string) => void;
}

/**
 * Two-step OTP credential flow reused for first-time set-password, forgot-password,
 * and self-service unlock. Step 1 requests a code (generic ack — anti-enumeration);
 * step 2 confirms with the code (+ a new password for setup/forgot). None auto-login.
 */
export function CredentialFlow({ variant, initialEmail = "", onBack, onDone }: Props) {
  const cfg = VARIANTS[variant];
  const [email, setEmail] = useState(initialEmail);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const requestMutation = useMutation({
    mutationFn: () => cfg.request(email.trim()),
    onSuccess: (res) => {
      setCodeSent(true);
      toast.success(res.message || "If the account exists, a code has been sent.");
    },
    onError: (err: Error) => toast.error(err.message || "Couldn't send the code — try again"),
  });

  const confirmMutation = useMutation({
    mutationFn: () => cfg.confirm(email.trim(), code, newPassword),
    onSuccess: (res) => {
      toast.success(res.message || "Done. Please sign in.");
      onDone(email.trim());
    },
    // 422 carries password-policy violations in the problem detail; surface the
    // server's exact rules (authoritative) rather than a generic message.
    onError: (err: Error) =>
      toast.error(
        policyViolationMessage(err) || err.message || "Couldn't complete — check the code/password",
      ),
  });

  const canRequest = /.+@.+\..+/.test(email) && !requestMutation.isPending;
  const canConfirm =
    code.length === 6 &&
    (!cfg.needsPassword || assessPassword(newPassword).allMet) &&
    !confirmMutation.isPending;

  return (
    <Card className="w-full max-w-md">
      <h1 className="font-display text-display-lg text-charcoal">{cfg.title}</h1>
      <p className="text-body-sm text-ink mt-2">{cfg.intro}</p>

      {!codeSent ? (
        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canRequest) requestMutation.mutate();
          }}
        >
          <div>
            <label className="text-body-sm text-ink" htmlFor="cred-email">
              Email
            </label>
            <Input
              id="cred-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button size="lg" fullWidth type="submit" disabled={!canRequest}>
            {requestMutation.isPending ? "Sending…" : "Send code"}
          </Button>
        </form>
      ) : (
        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) confirmMutation.mutate();
          }}
        >
          <div>
            <label className="text-body-sm text-ink">One-time code</label>
            <OTPInput value={code} onChange={setCode} onComplete={setCode} />
          </div>
          {cfg.needsPassword ? (
            <div>
              <label className="text-body-sm text-ink" htmlFor="cred-password">
                New password
              </label>
              <PasswordField
                id="cred-password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <PasswordStrength value={newPassword} />
            </div>
          ) : null}
          <Button size="lg" fullWidth type="submit" disabled={!canConfirm}>
            {confirmMutation.isPending ? "Submitting…" : "Confirm"}
          </Button>
          <button
            type="button"
            onClick={() => requestMutation.mutate()}
            disabled={requestMutation.isPending}
            className="text-caption text-forest block w-full text-center underline disabled:opacity-50"
          >
            Resend code
          </button>
        </form>
      )}

      <button
        type="button"
        onClick={onBack}
        className="text-caption text-muted hover:text-ink mt-4 block w-full text-center underline"
      >
        Back to sign in
      </button>
    </Card>
  );
}
